import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    createContenaire,
    deleteContenaire,
    deleteMultipleContenaires,
    getContenairesList,
    getAllContenairesList,
} from '../../../api/redux/slices/SuperAdmin';
import { icons } from '../../../assets/icons/index';
import AddContenaire from './AddContenaire';
import './index.scss';
import UpdateContenaire from './UpdateContenaire';
import { CSVLink } from 'react-csv';
import { useParams } from 'react-router-dom';
import { LoaderComponent } from '../../../helpers/components/loader';
import Papa from 'papaparse';
import { DataErrorModal, showToast } from '../../../components/modal';
import Pagination from '../../../helpers/components/Pagination';
import DeleteConfirmationModal from '../../admin/addUser/ShopDeleteModal';

const ContenairePage = () => {
    const dispatch = useDispatch();
    const params = useParams();

    const [page, setPage] = useState(Number(params.page) || 1);
    const [total, setTotal] = useState(0);
    const [conteneurs, setConteneurs] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [delModalOpen, setDelModalOpen] = useState(false);
    const [lineIndice, setLineIndice] = useState(null);
    const [typing, setTyping] = useState(false);
    const [newArr, setNewArr] = useState([]);
    const [isCheck, setIsCheck] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [errorData, setErrorData] = useState([]);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isloading, setIsLoading] = useState(false);

    const token = localStorage.getItem('accesstoken');
    const fileInputRef = useRef(null);

    // ─── Derived state ────────────────────────────────────────────────────────
    const checkedItems = newArr.filter((item) => item.checked);

    const headers = [
        { label: 'NOM', key: 'nom' },
        { label: 'VOLUME', key: 'volume' },
    ];

    // Export data: checked items if any are selected, otherwise all items on current page
    const csvLinkRef = useRef(null);
    const [csvData, setCsvData] = useState([]);

    const handleExport = async () => {
        // If items are checked, export only those
        if (checkedItems.length > 0) {
            setCsvData(checkedItems);
            setTimeout(() => csvLinkRef.current?.link?.click(), 100);
            return;
        }
        // Otherwise fetch ALL contenaires (no pagination) and export everything
        dispatch(getAllContenairesList({ token }))
            .unwrap()
            .then((res) => {
                const all = res.data || [];
                if (all.length === 0) {
                    showToast('Aucun contenaire à exporter', 'warn');
                    return;
                }
                setCsvData(all);
                setTimeout(() => csvLinkRef.current?.link?.click(), 100);
            })
            .catch(() => showToast("Erreur lors de l'export", 'error'));
    };

    // ─── Fetch helpers ────────────────────────────────────────────────────────
    // Plain function — no useCallback — so it never participates in
    // a useEffect dependency chain and cannot cause an infinite loop.
    const fetchList = (overridePage, overrideSearch) => {
        const activePage = overridePage !== undefined ? overridePage : page;
        const activeSearch = overrideSearch !== undefined ? overrideSearch : searchTerm;

        setIsLoading(true);
        dispatch(
            getContenairesList({
                token,
                search: activeSearch || undefined,
                page: activeSearch ? undefined : activePage,
            }),
        )
            .unwrap()
            .then((res) => {
                setConteneurs(res.data || []);
                setTotal(res.total || 0);
            })
            .catch(() => showToast("Quelque chose s'est mal passé !", 'error'))
            .finally(() => setIsLoading(false));
    };

    // ─── Effects ──────────────────────────────────────────────────────────────
    // Fetch on page change (only when NOT searching)
    useEffect(() => {
        if (!token || searchTerm) return;
        fetchList(page, '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, token]);

    // Fetch on search term change
    useEffect(() => {
        if (!token) return;
        // When user clears search, fall back to paginated fetch
        fetchList(page, searchTerm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, token]);

    // Sync Redux conteneurs → local newArr
    useEffect(() => {
        if (conteneurs && conteneurs.length > 0) {
            setNewArr(conteneurs.map((el) => ({ ...el, checked: false })));
        } else {
            setNewArr([]);
        }
    }, [conteneurs]);

    // Keep "check-all" checkbox in sync with individual rows
    useEffect(() => {
        if (newArr.length > 0) {
            setIsCheck(newArr.every((item) => item.checked));
        } else {
            setIsCheck(false);
        }
    }, [newArr]);

    // ─── File import ──────────────────────────────────────────────────────────
    const getfile = () => {
        fileInputRef.current.value = null;
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        const reader = new FileReader();
        reader.onload = async ({ target }) => {
            const csv = Papa.parse(target.result, { header: true });
            const parsedData = csv?.data || [];
            const errors = [];

            const promises = parsedData.reduce((acc, values) => {
                const data = {
                    nom: values.NOM,
                    volume: parseFloat(values.VOLUME) || 0,
                };
                if (data.nom) {
                    acc.push(
                        dispatch(createContenaire({ token, data }))
                            .unwrap()
                            .catch((err) => {
                                errors.push({
                                    Nom: data.nom,
                                    error: err?.error || err?.message,
                                });
                            }),
                    );
                }
                return acc;
            }, []);

            await Promise.all(promises);

            if (errors.length > 0) {
                setErrorData(errors);
                setErrorModalOpen(true);
            } else {
                showToast('Contenaires ajoutés', 'success');
            }

            fetchList();
        };
        reader.readAsText(selectedFile);
    };

    // ─── Delete handlers ──────────────────────────────────────────────────────
    const handleDelete = () => {
        dispatch(deleteContenaire({ token, id: lineIndice }))
            .unwrap()
            .then(() => {
                showToast('Contenaire supprimé', 'info');
                fetchList();
            })
            .catch(() => showToast("Erreur lors de la suppression", 'error'));
    };

    const handleDeleteSelected = () => {
        const ids = checkedItems.map((c) => c._id);
        if (ids.length === 0) return;

        dispatch(deleteMultipleContenaires({ token, ids }))
            .unwrap()
            .then(() => {
                showToast('Contenaires supprimés', 'info');
                fetchList();
            })
            .catch(() => showToast("Erreur lors de la suppression", 'error'));
    };

    // ─── Checkbox handlers ────────────────────────────────────────────────────
    const handleCheckAllClick = () => {
        const next = !isCheck;
        setIsCheck(next);
        setNewArr((prev) => prev.map((item) => ({ ...item, checked: next })));
    };

    const handleCheckClick = (id) => {
        setNewArr((prev) =>
            prev.map((item) => (item._id === id ? { ...item, checked: !item.checked } : item)),
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div id='contenairePage'>
            {/* Bulk-delete confirmation */}
            {showDeleteModal && (
                <DeleteConfirmationModal
                    onDelete={() => {
                        handleDeleteSelected();
                        setShowDeleteModal(false);
                    }}
                    onCancel={() => setShowDeleteModal(false)}
                    msg={
                        checkedItems.length > 1
                            ? 'Êtes-vous sûr de vouloir supprimer tous les contenaires sélectionnés ?'
                            : 'Êtes-vous sûr de vouloir supprimer ce contenaire ?'
                    }
                />
            )}

            {/* Navbar */}
            <div className='navbar'>
                <div className='left'>
                    <div className='nav-search'>
                        <input
                            type='text'
                            placeholder='Rechercher ...'
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setTyping(true);
                            }}
                        />
                        <icons.FiSearch className='searchicon' />
                        {typing && (
                            <icons.GrFormClose
                                className='searchicon'
                                onClick={() => {
                                    setSearchTerm('');
                                    setTyping(false);
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className='right'>
                    <div className='right-items'>
                        <button
                            className='button-primary'
                            onClick={() => setAddModalOpen(!addModalOpen)}
                        >
                            <icons.SiAddthis />
                            <span>Contenaire</span>
                        </button>
                        <CSVLink
                            ref={csvLinkRef}
                            data={csvData}
                            headers={headers}
                            filename='Contenaires.csv'
                            style={{ display: 'none' }}
                        />
                        <button
                            className='button-nature'
                            title={
                                checkedItems.length > 0
                                    ? `Exporter ${checkedItems.length} sélectionné(s)`
                                    : 'Exporter tous les contenaires'
                            }
                            onClick={handleExport}
                        >
                            <icons.BiExport />
                        </button>
                        <button
                            className='button-nature'
                            title='importer contenaires'
                            onClick={getfile}
                        >
                            <icons.BiImport />
                            <input
                                type='file'
                                id='fileuploading'
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept='.csv'
                                onChange={handleFileChange}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* Header */}
            <div id='head'>
                <div className='title-user'>
                    <div>
                        <h1>Contenaires</h1>
                    </div>
                    <div
                        className='deluser'
                        onClick={() => {
                            if (checkedItems.length > 0) {
                                setShowDeleteModal(true);
                            } else {
                                showToast('Choisissez au moins un contenaire.', 'warn');
                            }
                        }}
                    >
                        <icons.FaTrashAlt className='del-contenaire' />
                    </div>
                </div>
            </div>

            {/* Modals */}
            <AddContenaire
                addModalOpen={addModalOpen}
                setAddModalOpen={setAddModalOpen}
                onSuccess={fetchList}
            />

            {modalOpen && (
                <UpdateContenaire
                    modalOpen={modalOpen}
                    setModalOpen={setModalOpen}
                    lineIndice={lineIndice}
                    onSuccess={fetchList}
                />
            )}

            {delModalOpen && (
                <DeleteConfirmationModal
                    onDelete={() => {
                        handleDelete();
                        setDelModalOpen(false);
                    }}
                    onCancel={() => setDelModalOpen(false)}
                    msg={'Êtes-vous sûr de vouloir supprimer ce contenaire ?'}
                />
            )}

            {/* Table */}
            <div id='contenairesContent'>
                <table id='contenaires'>
                    <thead>
                        <tr id='titres'>
                            <td className='input-check'>
                                <input
                                    type='checkbox'
                                    className='checkbox'
                                    checked={isCheck}
                                    onChange={handleCheckAllClick}
                                />
                            </td>
                            <td>NOM</td>
                            <td>VOLUME</td>
                            <td>ACTION</td>
                        </tr>
                    </thead>
                    <tbody>
                        {isloading ? (
                            <tr>
                                <td colSpan='4'>
                                    <LoaderComponent margin='10%' />
                                </td>
                            </tr>
                        ) : newArr.length > 0 ? (
                            newArr.map((contenaire, index) => (
                                <tr
                                    id={`element-${index}`}
                                    key={contenaire._id}
                                    className='contenaire-row'
                                >
                                    <td className='input-check'>
                                        <input
                                            type='checkbox'
                                            checked={contenaire.checked}
                                            className='checkbox'
                                            onChange={() => handleCheckClick(contenaire._id)}
                                        />
                                    </td>
                                    <td>{contenaire.nom}</td>
                                    <td>{contenaire.volume} m³</td>
                                    <td className='last-contenaire-element'>
                                        <icons.FaRegEdit
                                            className='edit-contenaire'
                                            onClick={() => {
                                                setModalOpen(true);
                                                setLineIndice(contenaire._id);
                                            }}
                                        />
                                        <icons.FaTrashAlt
                                            className='del-contenaire'
                                            onClick={() => {
                                                setDelModalOpen(true);
                                                setLineIndice(contenaire._id);
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan='4'
                                    style={{ textAlign: 'center', padding: '50px' }}
                                >
                                    Aucun contenaire trouvé
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Pagination
                page={page}
                setPage={setPage}
                pageLink={`/superadmin/contenaires/`}
                total={total}
            />

            <DataErrorModal
                isOpen={errorModalOpen}
                closeModal={() => setErrorModalOpen(false)}
                errorData={errorData}
                width='35rem'
                height='auto'
            />
        </div>
    );
};

export default ContenairePage;